import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
} from 'firebase/auth';

import firebase from '@/shared/services/firebase';

const auth = firebase.auth;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileRefreshCallbacks, setProfileRefreshCallbacks] = useState([]);

    // Centralized profile existence check. Prevents both PublicRoutes and
    // ProtectedRoutes from independently calling /api/users/me.
    // null = unknown/not applicable, true = profile exists, false = no profile
    const [hasProfile, setHasProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    // Monotonic counter that signals consumers (e.g. useFullAuth) to re-fetch
    // the MongoDB profile. Increment via invalidateProfile().
    const [profileVersion, setProfileVersion] = useState(0);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Check if the authenticated user has a MongoDB profile.
    // Runs when user or email verification status changes.
    useEffect(() => {
        if (!user || !user.emailVerified) {
            setHasProfile(null);
            return;
        }

        // Use fetch directly instead of the axios instance to avoid a
        // circular dependency (axiosInstance imports AuthContext).
        const checkProfile = async () => {
            setProfileLoading(true);
            try {
                const token = await user.getIdToken();
                const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                const res = await fetch(`${baseUrl}/api/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: 'include',
                });
                if (res.ok) {
                    setHasProfile(true);
                } else if (res.status === 404 || res.status === 401) {
                    setHasProfile(false);
                } else {
                    setHasProfile(null);
                }
            } catch {
                // On network error, don't block: let them through
                setHasProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };

        checkProfile();
    }, [user]);

    const login = (email, password) =>
        signInWithEmailAndPassword(auth, email, password);

    const signup = async (email, password) => {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
        );

        const user = userCredential.user;

        // Send verification email
        await sendEmailVerification(user);
        await user.getIdToken();

        return user;
    };

    const logout = () => signOut(auth);

    const reloadCurrentUser = async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            // Force update by manually triggering the auth state change
            setUser(auth.currentUser);
        }
    };

    const registerProfileRefreshCallback = useCallback((callback) => {
        setProfileRefreshCallbacks((prev) => [...prev, callback]);
        // Return unregister function
        return () => {
            setProfileRefreshCallbacks((prev) =>
                prev.filter((cb) => cb !== callback)
            );
        };
    }, []);

    // Called after profile creation to immediately update hasProfile
    // without waiting for a re-check
    const refreshProfileStatus = useCallback(() => {
        setHasProfile(true);
        profileRefreshCallbacks.forEach((callback) => callback());
    }, [profileRefreshCallbacks]);

    // Bumps profileVersion so useFullAuth (and anything else watching it)
    // re-fetches the MongoDB profile on the next render cycle.
    const invalidateProfile = useCallback(() => {
        setProfileVersion((v) => v + 1);
    }, []);

    const resetPassword = (email) => sendPasswordResetEmail(auth, email);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                hasProfile,
                profileLoading,
                profileVersion,
                login,
                signup,
                logout,
                resetPassword,
                reloadCurrentUser,
                registerProfileRefreshCallback,
                refreshProfileStatus,
                invalidateProfile,
            }}
        >
            {loading ? (
                // A spinner rather than nothing. Rendering `false` paints a
                // blank page with an empty #root and no console error for as
                // long as auth state is unresolved, which is indistinguishable
                // from a crashed build. EcosystemProvider, the sibling that
                // wraps this one, renders this same spinner for the same
                // situation; both must agree on what "loading" looks like.
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </Spinner>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
